// WipView — Work-In-Progress dashboard
// Mirkov roadmap HIGH: per zákazka VV / vyfakturované / náklady / zisk forecast
// Zoradenie: straty hore (najrýchlejšie nájde problémové zákazky)
import React, { useEffect, useMemo, useState } from 'react'
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { useNavigate } from 'react-router-dom'
import { PageHeader, KpiRow, KpiCard, SearchInput, FilterChips, EmptyPane } from '../components/Ui.jsx'
import { useBreadcrumb } from '../components/Layout.jsx'
import { useToast } from '../components/Toast.jsx'
import { COLORS, TYPO, SPACE, RADIUS, SK } from '../theme.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'

const ROLY_WIP = ['admin', 'owner', 'owner_view', 'office_manager', 'accountant']

function noDiac(s) {
  return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

export default function WipView() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const { setTrail } = useBreadcrumb()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')  // all | strata | zisk

  useEffect(() => {
    setTrail([{ label: 'Matrix v2', onClick: () => navigate('/') }, { label: 'WIP — aktívne zákazky' }])
    return () => setTrail([])
  }, [setTrail, navigate])

  function nacitaj() {
    setLoading(true)
    fetchAuth('/api/wip/dashboard')
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(e => toast.error('Chyba: ' + e.message))
      .finally(() => setLoading(false))
  }
  useEffect(nacitaj, [])
  useLiveRefresh(() => fetchAuth('/api/wip/dashboard').then(r => r.ok ? r.json() : null).then(d => setData(d)).catch(() => {})) // živé (ticho, bez spinnera)

  // ⚠ Rules of Hooks — useMemo MUSÍ byť pred conditional returns
  const items = useMemo(() => {
    if (!data || !Array.isArray(data.items)) return []
    let arr = data.items
    if (filter === 'strata') arr = arr.filter(z => z.zisk_forecast < 0)
    else if (filter === 'zisk') arr = arr.filter(z => z.zisk_forecast >= 0)
    if (search.trim()) {
      const q = noDiac(search)
      arr = arr.filter(z => noDiac(z.name).includes(q) || noDiac(z.id).includes(q))
    }
    return arr
  }, [data, filter, search])

  if (user && !ROLY_WIP.includes(user.role)) {
    return (
      <div style={{ padding: SPACE.xl }}>
        <PageHeader title="WIP — aktívne zákazky" />
        <EmptyPane icon="" text="Bez prístupu — túto stránku vidí len admin, majiteľ, office a účtovníčka." />
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ padding: SPACE.xl }}>
        <PageHeader title="WIP — aktívne zákazky" />
        <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>Načítavam zákazky…</div>
      </div>
    )
  }
  if (!data) {
    return (
      <div style={{ padding: SPACE.xl }}>
        <PageHeader title="WIP — aktívne zákazky" />
        <EmptyPane icon="" text="Žiadne dáta — skús obnoviť stránku." />
      </div>
    )
  }

  return (
    <div style={{ padding: SPACE.xl, display: 'flex', flexDirection: 'column', gap: SPACE.md, height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      <PageHeader
        title="WIP — aktívne zákazky"
        subtitle="Prehľad: VV / vyfakturované / náklady / zisk forecast — straty hore"
      >
        <button
          onClick={nacitaj}
          style={{
            padding: '6px 14px',
            border: `1px solid ${COLORS.line}`,
            background: COLORS.bg,
            borderRadius: RADIUS.sm,
            cursor: 'pointer',
            fontSize: TYPO.sizes.sm,
          }}
        >Obnoviť</button>
      </PageHeader>

      {/* KPI súhrn */}
      <KpiRow cols={4}>
        <KpiCard title="VV celkom" value={SK.eur(data.summary.vv_total)} />
        <KpiCard title="Fakturované" value={SK.eur(data.summary.fakt_total)} color={COLORS.add} />
        <KpiCard title="Náklady" value={SK.eur(data.summary.naklady_total)} color={COLORS.remove} />
        <KpiCard
          title="Zisk forecast"
          value={SK.eur(data.summary.zisk_forecast_total)}
          color={data.summary.zisk_forecast_total >= 0 ? COLORS.add : COLORS.remove}
        />
      </KpiRow>

      {data.summary.riskove_count > 0 && (
        <div style={{
          padding: SPACE.md,
          background: 'rgba(239,68,68,0.08)',
          border: `1px solid ${COLORS.remove}`,
          borderRadius: RADIUS.md,
          fontSize: TYPO.sizes.sm,
          color: COLORS.remove,
        }}>
          <strong>{data.summary.riskove_count} zákaziek je v strate</strong> (forecast: náklady prevyšujú VV). Pozri tabuľku — sú zoradené hore.
        </div>
      )}

      {/* Filter + hľadanie */}
      <div style={{ display: 'flex', gap: SPACE.md, alignItems: 'center', flexWrap: 'wrap' }}>
        <FilterChips
          value={filter}
          onChange={setFilter}
          options={[
            { key: 'all', label: `Všetky (${data.items.length})`, color: COLORS.muted },
            { key: 'strata', label: `Stratové (${data.summary.riskove_count})`, color: COLORS.remove },
            { key: 'zisk', label: `Ziskové (${data.items.length - data.summary.riskove_count})`, color: COLORS.add },
          ]}
        />
        <div style={{ flex: 1, minWidth: 200 }}>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Hľadaj zákazku (názov alebo ID)…"
          />
        </div>
      </div>

      {/* Tabuľka */}
      {items.length === 0 ? (
        <EmptyPane icon="" text="Žiadne zákazky — pre tento filter nie sú výsledky." />
      ) : (
        <div style={{
          background: COLORS.bg,
          border: `1px solid ${COLORS.line}`,
          borderRadius: RADIUS.md,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '90px 1fr 110px 110px 110px 110px 90px 90px',
            gap: SPACE.sm,
            padding: '10px 14px',
            background: COLORS.bgSoft,
            borderBottom: `1px solid ${COLORS.line}`,
            fontSize: TYPO.sizes.xs,
            color: COLORS.muted,
            textTransform: 'uppercase',
            fontWeight: TYPO.weights.semibold,
          }}>
            <div>ID</div>
            <div>Názov zákazky</div>
            <div style={{ textAlign: 'right' }}>VV</div>
            <div style={{ textAlign: 'right' }}>Fakturované</div>
            <div style={{ textAlign: 'right' }}>Náklady</div>
            <div style={{ textAlign: 'right' }}>Zisk forecast</div>
            <div style={{ textAlign: 'right' }}>Čerpanie</div>
            <div style={{ textAlign: 'center' }}>Stav</div>
          </div>
          {items.map(z => (
            <div
              key={z.id}
              onClick={() => navigate(`/zakazky/${z.id}`)}
              style={{
                display: 'grid',
                gridTemplateColumns: '90px 1fr 110px 110px 110px 110px 90px 90px',
                gap: SPACE.sm,
                padding: '10px 14px',
                borderBottom: `1px solid ${COLORS.line}`,
                cursor: 'pointer',
                background: z.riskovy ? 'rgba(239,68,68,0.05)' : 'transparent',
                borderLeft: z.riskovy ? `4px solid ${COLORS.remove}` : '4px solid transparent',
                fontSize: TYPO.sizes.sm,
                alignItems: 'center',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = z.riskovy ? 'rgba(239,68,68,0.12)' : COLORS.bgSoft}
              onMouseLeave={(e) => e.currentTarget.style.background = z.riskovy ? 'rgba(239,68,68,0.05)' : 'transparent'}
            >
              <div style={{ fontFamily: TYPO.mono, color: COLORS.muted, fontSize: TYPO.sizes.xs }}>{z.id}</div>
              <div style={{ fontWeight: TYPO.weights.semibold }}>{z.name || '(bez názvu)'}</div>
              <div style={{ textAlign: 'right', fontFamily: TYPO.mono }}>{SK.eur(z.vv)}</div>
              <div style={{ textAlign: 'right', fontFamily: TYPO.mono, color: COLORS.add }}>{SK.eur(z.fakturovane)}</div>
              <div style={{ textAlign: 'right', fontFamily: TYPO.mono, color: COLORS.remove }}>{SK.eur(z.naklady)}</div>
              <div style={{
                textAlign: 'right',
                fontFamily: TYPO.mono,
                fontWeight: TYPO.weights.bold,
                color: z.zisk_forecast >= 0 ? COLORS.add : COLORS.remove,
              }}>{SK.eur(z.zisk_forecast)}</div>
              <div style={{ textAlign: 'right', fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{z.cerpanie_pct}%</div>
              <div style={{ textAlign: 'center' }}>
                <span style={{
                  fontSize: TYPO.sizes.xs,
                  padding: '2px 8px',
                  borderRadius: RADIUS.full,
                  background: z.status === 'active' ? 'rgba(34,197,94,0.12)' : COLORS.bgSoft,
                  color: z.status === 'active' ? COLORS.add : COLORS.muted,
                }}>{z.status === 'active' ? 'Aktívna' : z.status === 'planned' ? 'Plánovaná' : z.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Vysvetlivka — ústava 4 (samovysvetľujúci systém) */}
      <div style={{
        padding: SPACE.md,
        background: COLORS.bgSoft,
        borderRadius: RADIUS.md,
        fontSize: TYPO.sizes.xs,
        color: COLORS.muted,
      }}>
        <strong>Ako čítať:</strong> <b>VV</b> = hodnota zo zmluvy (rozpočet). <b>Fakturované</b> = už vystavené faktúry zákazníkovi.
        <b> Náklady</b> = výdavky na zákazku (VSZAK + VSOST + mzdy). <b>Zisk forecast</b> = VV − náklady (predpoveď).
        Stratové (forecast záporný) sú vždy hore.
      </div>
    </div>
  )
}
